import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { queryClient } from "./lib/queryClient";
import { useAuthStore } from "./stores/useAuthStore";
import { ActivityTrackingProvider } from "./contexts/ActivityTrackingContext";
import { ROUTES } from "./constants/routes";
import type { UserRole } from "./constants/roles";
import ProtectedRoute from "@/constants/ProtectedRoute";
import GuestRoute from "@/constants/GuestRoute";
import RoleBasedRoute from "@constants/RoleBasedRoute";
import DashboardRouter from "@/constants/DashboardRouter/DashboardRouter";
import PublicLayout from "@/PageLayout/PublicLayout/MainLayout";
import PrivateLayout from "@/PageLayout/PrivateLayout/Layout/LoggedInLayout";
import "./styles/globals.css";

function AppContent() {
  const navigate = useNavigate();
  const checkTokenExpiry = useAuthStore((state) => state.checkTokenExpiry);
  const logout = useAuthStore((state) => state.logout);

  // Initialize auth - start proactive token refresh timer
  useEffect(() => {
    checkTokenExpiry();
  }, [checkTokenExpiry]);

  // Global auth event listeners - handle session expiry and access control
  useEffect(() => {
    // Re-entrancy guard. The httpClient dispatches `auth:logout` whenever a
    // 401 can't be recovered. If our handler itself calls `logout()` (which
    // POSTs /auth/logout) and that call also 401s, the event fires again.
    // Without this flag we'd loop. With it, the second event is a no-op.
    let logoutInFlight = false;

    const handleLogout = async (event: Event) => {
      if (logoutInFlight) return;
      logoutInFlight = true;

      const isPublicPage = window.location.pathname.startsWith('/feedback/');
      // The httpClient pre-clears tokens for the /auth/logout-401 case, so
      // calling `logout()` here would re-POST and likely 401 again. Run the
      // local-only cleanup path instead and reset the in-flight flag once
      // the auth state is settled.
      try {
        await logout();
      } catch (e) {
        // Local cleanup happens in the store's `finally` — safe to ignore.
      }

      if (isPublicPage) {
        logoutInFlight = false;
        return;
      }

      // Notify the user (the original 401 was silent before this fix).
      const reason = (event as CustomEvent)?.detail?.reason
        || 'Your session has expired. Please log in again.';
      toast.warn(reason, { toastId: 'auth-expired', autoClose: 4000 });

      navigate('/login', {
        replace: true,
        state: { message: reason },
      });

      logoutInFlight = false;
    };

    const handleForbidden = () => {
      // Don't redirect from public pages (e.g., feedback forms accessible while logged in)
      if (window.location.pathname.startsWith('/feedback/')) return;

      const { user } = useAuthStore.getState();
      if (user) {
        // Import dynamically to avoid circular dependencies
        import("./constants/roles").then(
          ({ getDefaultRouteForRole, getRoleFromString }) => {
            const userRole = getRoleFromString(user.role);
            const currentPath = window.location.pathname;

            // Check if user is already on a path that matches their role
            // Don't redirect if they're already in their role's section
            if (
              (userRole === 'admin' && currentPath.startsWith('/admin/')) ||
              (userRole === 'trainee' && currentPath.startsWith('/trainee/')) ||
              (userRole === 'trainer' && currentPath.startsWith('/trainer/')) ||
              (userRole === 'super_admin' && currentPath.startsWith('/superadmin/'))
            ) {
              // User is already in their correct section, don't redirect
              // The 403 is likely from an API call, not a route permission issue
              console.log('🔐 403 error but user is already in correct section, not redirecting');
              return;
            }

            // Only redirect if user is trying to access a different role's section
            const defaultRoute = getDefaultRouteForRole(userRole);
            navigate(defaultRoute, {
              replace: true,
              state: {
                message: "You do not have permission to access that page.",
              },
            });
          },
        );
      }
    };

    // Add event listeners
    window.addEventListener("auth:logout", handleLogout);
    window.addEventListener("auth:forbidden", handleForbidden);

    // Cleanup
    return () => {
      window.removeEventListener("auth:logout", handleLogout);
      window.removeEventListener("auth:forbidden", handleForbidden);
    };
  }, [navigate, logout]);

  return (
    <ActivityTrackingProvider>
      <Routes>
        {/* Public Routes - Auto-generated from ROUTES config */}
        {ROUTES.filter((r) => r.isPublic).map((route) => {
          const Component = route.component;
          const needsMainLayout = [
            "/",
            "/features",
            "/pricing",
            "/about",
            "/contact",
          ].includes(route.path);

          // Pages that authenticated users should not access
          const guestOnlyPages = [
            "/",
            "/landing",
            "/login",
            "/forgot-password",
            "/verify-otp",
            "/create-new-password",
          ];
          const shouldRedirectIfAuthenticated = guestOnlyPages.includes(
            route.path,
          );

          const element = needsMainLayout ? (
            <PublicLayout>
              <Component />
            </PublicLayout>
          ) : (
            <Component />
          );

          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                shouldRedirectIfAuthenticated ? (
                  <GuestRoute>{element}</GuestRoute>
                ) : (
                  element
                )
              }
            />
          );
        })}

        {/* Protected /workspace route - redirects to role-specific dashboard */}
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          }
        />

        {/* Protected Role-Specific Routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <PrivateLayout />
            </ProtectedRoute>
          }
        >
          {ROUTES.filter(
            (r) => !r.isPublic && !r.path.startsWith("/workspace"),
          ).map((route) => {
            const Component = route.component;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  route.allowedRoles && route.allowedRoles.length > 0 ? (
                    <RoleBasedRoute allowedRoles={route.allowedRoles}>
                      <Component />
                    </RoleBasedRoute>
                  ) : (
                    <Component />
                  )
                }
              />
            );
          })}
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ActivityTrackingProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppContent />
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="colored"
        />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
